import { useState } from 'react';
import { Cumplimiento } from '../components/Cumplimiento.tsx';
import { useLoaded } from '../lib/useLoaded.ts';
import { LoadingNotice, EmptyNotice, ErrorNotice } from '../components/StateNotice.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { navigate } from '../lib/router.ts';
import type { DataPort } from '../adapters/port.ts';

/**
 * Centro Operativo. Métricas DERIVADAS del adaptador —ninguna cifra escrita a
 * mano— y, debajo, la Hoja de Ruta: la guía permanente de cómo funciona el
 * sistema de punta a punta.
 *
 * El feed de actividad se retiró de acá: era un bloque largo de texto que
 * rompía la composición y no aportaba valor inmediato al entrar. El historial
 * sigue existiendo y auditándose; simplemente no compite con lo que el usuario
 * necesita ver primero.
 */
export function Dashboard({ port }: { port: DataPort }) {
  const m = useLoaded(() => port.metrics());

  return (
    <section>
      <p className="lede">
        Estado del estudio de un vistazo: pedidos activos, dónde está cada pieza en el pipeline y qué
        espera tu decisión. Todo bajo control humano.
      </p>

      {m.kind === 'loading' && <LoadingNotice what="las métricas" />}
      {m.kind === 'error' && <ErrorNotice message={m.message} />}
      {m.kind === 'empty' && <EmptyNotice what="métricas" />}
      {m.kind === 'ok' && (
        <div className="kpis" data-testid="kpis">
          {/* Colores por URGENCIA: rojo = requiere tu acción inmediata,
              amarillo = algo listo para retirar, neutro = informativo.
              Cuatro tarjetas, no cinco: la quinta caía sola a una segunda fila
              y rompía la alineación. «Etapas bloqueadas» sigue visible donde se
              actúa sobre ella —el Creative Pipeline—, que es su lugar natural. */}
          <Kpi label="Proyectos activos" value={m.value.activeProjects} to="/pipeline" />
          <Kpi label="Aprobaciones pendientes" value={m.value.pendingApprovals} to="/approvals"
               tone={m.value.pendingApprovals > 0 ? 'danger' : undefined} />
          <Kpi label="Entregables recientes" value={m.value.recentDeliverables} to="/library"
               tone={m.value.recentDeliverables > 0 ? 'warn' : undefined} />
          <Kpi label="Integraciones con problemas" value={m.value.failingIntegrations} to="/integrations"
               tone={m.value.failingIntegrations > 0 ? 'danger' : undefined} />
        </div>
      )}

      <HojaDeRuta />

      {/* La deuda más vieja del sistema: comprometer una fecha y MEDIR si se
          cumplió. Va en el panel porque es lo primero que Dirección pidió. */}
      <Cumplimiento port={port} />
    </section>
  );
}

function Kpi({ label, value, to, tone }: {
  label: string; value: number; to: string; tone?: 'warn' | 'danger' | undefined;
}) {
  return (
    <a className={'kpi kpi--link' + (tone ? ` kpi--${tone}` : '')} href={'#' + to}
       aria-label={`${label}: ${value}. Ir a la sección`}>
      <span className="kpi__value">{value}</span>
      <span className="kpi__label">{label}</span>
    </a>
  );
}

/* ── Hoja de Ruta ──────────────────────────────────────────────────────────
   Guía de onboarding permanente, dentro del Dashboard. Contesta de un vistazo
   dónde está el usuario, qué puede hacer, qué espera el sistema y qué obtiene
   al final — sin necesidad de leer documentación aparte. */

interface Etapa {
  readonly n: string;
  readonly nombre: string;
  readonly titulo: string;
  readonly bajada: string;
  readonly hace: string;
  readonly obtenes: string;
}

const ETAPAS: readonly Etapa[] = [
  { n: '01', nombre: 'Brief', titulo: 'Contás qué necesitás',
    bajada: 'En tus palabras, sin formato ni tecnicismos. No hace falta que sea preciso.',
    hace: 'Lee el pedido, identifica el entregable concreto y la necesidad real detrás. Si no lo reconoce, lo dice en vez de inventarlo.',
    obtenes: 'Tu pedido registrado tal cual, con un código para seguirlo.' },
  { n: '02', nombre: 'Alcance', titulo: 'El sistema arma el plan y vos lo aprobás',
    bajada: 'Nada arranca sin tu OK explícito. Acá se define todo lo que va a pasar.',
    hace: 'Define entregable, nivel, complejidad, qué plataformas usa y el roadmap paso a paso con su duración. Si no entra en una hora, te lo plantea y lo justifica.',
    obtenes: 'Un plan concreto para aprobar o rechazar. Si el plazo no te sirve, te propone una versión con menos alcance.' },
  { n: '03', nombre: 'Producción', titulo: 'El motor produce la pieza',
    bajada: 'Recién acá arranca el reloj: desde tu aprobación, no desde el envío.',
    hace: 'Ejecuta el roadmap aprobado con el motor creativo, etapa por etapa, registrando el tiempo real de cada una.',
    obtenes: 'Avance observable en el Creative Pipeline, con el progreso derivado de las etapas reales.' },
  { n: '04', nombre: 'Revisión', titulo: 'Vos revisás lo que se produjo',
    bajada: 'La pieza queda lista para tu revisión dentro del plazo comprometido.',
    hace: 'Deja el entregable abrible, con su concepto creativo y la evidencia de qué modelo lo generó y cuándo.',
    obtenes: 'La pieza completa para abrir, revisar y decidir si sirve.' },
  { n: '05', nombre: 'Entrega', titulo: 'Entrega',
    bajada: 'Las piezas aprobadas quedan listas para usar y todo el proceso queda registrado.',
    hace: 'Cierra el ciclo, archiva las piezas finales y deja el historial disponible.',
    obtenes: 'Entregables finales + historial completo del brief.' },
];

const PRINCIPIOS: ReadonlyArray<{ icono: IconName; titulo: string; texto: string }> = [
  { icono: 'panel', titulo: 'Alcance mínimo suficiente',
    texto: 'El sistema no infla el pedido: elige sólo las herramientas necesarias para lo que pediste.' },
  { icono: 'shield-check', titulo: 'Siempre bajo decisión humana',
    texto: 'Nada se ejecuta, publica ni entrega sin que vos lo apruebes. El control nunca es automático.' },
  { icono: 'layers', titulo: 'Trazable y auditable',
    texto: 'Cada paso deja evidencia verificable. Sabés quién decidió qué, cuándo y por qué.' },
];

function HojaDeRuta() {
  const [sel, setSel] = useState(0);
  const e = ETAPAS[sel]!;

  return (
    <section className="hoja" aria-label="Hoja de ruta">
      <header className="hoja__head">
        <div className="ds-eyebrow">Hoja de ruta</div>
        <h2 className="hoja__title">¿Dónde estás y hacia dónde vas?</h2>
        <p className="hoja__sub">
          Nexus Creative Platform toma tu pedido y lo lleva de una idea a piezas terminadas.
          Cada etapa es observable y cada avance termina en una decisión tuya.
        </p>
      </header>

      {/* El recorrido, explorable: tocá una etapa para ver qué pasa ahí. */}
      <ol className="hoja__steps">
        {ETAPAS.map((x, i) => (
          <li key={x.n} className="hoja__step">
            <button
              type="button"
              className={'hoja__node' + (i === sel ? ' hoja__node--sel' : '') + (i < sel ? ' hoja__node--done' : '')}
              onClick={() => setSel(i)}
              aria-current={i === sel ? 'step' : undefined}
              aria-label={`Etapa ${x.n}: ${x.nombre}`}
            >
              {x.n}
            </button>
            <span className={'hoja__stepname' + (i === sel ? ' hoja__stepname--sel' : '')}>{x.nombre}</span>
          </li>
        ))}
      </ol>

      <article className="hoja__card">
        <div className="hoja__cardhead">
          <span className="hoja__num">{e.n}</span>
          <span className="hoja__badge">{sel === ETAPAS.length - 1 ? 'Cierre' : 'Etapa'}</span>
        </div>
        <h3 className="hoja__cardtitle">{e.titulo}</h3>
        <p className="hoja__cardsub">{e.bajada}</p>

        <div className="hoja__split">
          <div className="hoja__box">
            <div className="hoja__boxlabel">Qué hace el sistema</div>
            <p>{e.hace}</p>
          </div>
          <div className="hoja__box">
            <div className="hoja__boxlabel">Qué obtenés</div>
            <p>{e.obtenes}</p>
          </div>
        </div>
      </article>

      <div className="hoja__expect">
        <div className="ds-eyebrow">Qué esperar del sistema</div>
        <h3 className="hoja__title2">Una plataforma, no un generador suelto</h3>
        <div className="hoja__princ">
          {PRINCIPIOS.map((p) => (
            <article className="hoja__pcard" key={p.titulo}>
              <span className="hoja__picon"><Icon name={p.icono} size={18} /></span>
              <h4>{p.titulo}</h4>
              <p>{p.texto}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="hoja__cta">
        <button type="button" className="btn-primary hoja__btn" onClick={() => navigate('/brief')}>
          Empezar un brief <span aria-hidden="true">→</span>
        </button>
        <p className="hoja__foot">
          Nada se corrige ni se reinicia automáticamente. Cada acción termina en una decisión humana.
        </p>
      </div>
</section>
  );
}
